import { useEffect, useRef, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { SW_UPDATE_AVAILABLE_EVENT, type SwUpdateAvailableDetail } from '@/lib/swUpdate'

// People who don't notice/act on the prompt still get updated eventually,
// instead of running a stale build indefinitely.
const AUTO_RELOAD_MS = 90_000

export function UpdateAvailableBanner() {
  const { t } = useTranslation()
  const [reload, setReload] = useState<(() => Promise<void>) | null>(null)
  const reloadingRef = useRef(false)

  useEffect(() => {
    function onUpdateAvailable(event: Event) {
      const { reload } = (event as CustomEvent<SwUpdateAvailableDetail>).detail
      setReload(() => reload)
    }
    window.addEventListener(SW_UPDATE_AVAILABLE_EVENT, onUpdateAvailable)
    return () => window.removeEventListener(SW_UPDATE_AVAILABLE_EVENT, onUpdateAvailable)
  }, [])

  function handleReload() {
    if (reloadingRef.current || !reload) return
    reloadingRef.current = true
    void reload()
  }

  useEffect(() => {
    if (!reload) return
    const timer = setTimeout(handleReload, AUTO_RELOAD_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload])

  if (!reload) return null

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 14px',
        background: 'color-mix(in srgb, var(--brand) 10%, var(--surface))',
        borderBottom: '1px solid color-mix(in srgb, var(--brand) 20%, var(--border))',
      }}
    >
      <RefreshCw size={16} color="var(--brand)" strokeWidth={2} style={{ flexShrink: 0 }} />

      <span
        style={{
          flex: 1,
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-sm)',
          color: 'var(--text-body)',
          minWidth: 0,
        }}
      >
        {t('settings.update_available_text')}
      </span>

      <button
        type="button"
        onClick={handleReload}
        style={{
          border: 'none',
          background: 'none',
          padding: 0,
          cursor: 'pointer',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-sm)',
          fontWeight: 600,
          color: 'var(--brand)',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {t('settings.update_available_reload')}
      </button>
    </div>
  )
}
